import React, {useEffect, useRef, useState, RefObject} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {Link} from '../../Link';
import {banner} from '../../../css/topBanner.module.css';
import {SlimIcon} from '../../icon/SlimIcon';

export type HTMLScrolltEvent<T extends HTMLElement> = Event & {
  target: {
    scrollingElement: T;
  };
};

export function calcMarginTop(currentMarginTop: number, scrollY: number, previousScrollY: number, elementHeight: number): number {
  let newMarginTop = currentMarginTop + (previousScrollY - scrollY);

  newMarginTop = Math.max(-elementHeight, Math.min(0, newMarginTop));

  return newMarginTop;
}

export function listenToScroll(
  event: HTMLScrolltEvent<HTMLElement>,
  setMarginTop: React.Dispatch<React.SetStateAction<number>>,
  previousScrollY: React.MutableRefObject<number>,
  forwardedRef: React.RefObject<HTMLDivElement>
) {
  const scrollY = event.target.scrollingElement.scrollTop > 0 ? event.target.scrollingElement.scrollTop : 0;
  setMarginTop(currentMarginTop => calcMarginTop(currentMarginTop, scrollY, previousScrollY.current, forwardedRef.current.offsetHeight));
  previousScrollY.current = scrollY;
}

// We need ref.current.offsetHeight in the parent so it's forwarded from there
export const TopBanner = ({text, forwardedRef}: {text?: string; forwardedRef: RefObject<HTMLDivElement>}) => {
  const [marginTop, setMarginTop] = useState(0);
  const previousScrollY = useRef(0);

  useEffect(() => {
    let animationFrameId: number;

    const handleScroll = (event: HTMLScrolltEvent<HTMLElement>) => {
      animationFrameId = requestAnimationFrame(() => {
        listenToScroll(event, setMarginTop, previousScrollY, forwardedRef);
      });
    };
    window.addEventListener('scroll', handleScroll, {passive: true});

    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div
      ref={forwardedRef}
      className='bg-primary flex justify-center text-white h-full min-h-[56px]'
      style={{marginTop: `${marginTop}px`}}
      data-testid='top-banner'
    >
      {text ? <TopBannerCustomTextContent text={text} /> : <TopBannerDefaultContent />}
    </div>
  );
};

const TopBannerCustomTextContent = ({text}: {text: string}) => (
  <div className={banner} data-testid='custom-text-banner' dangerouslySetInnerHTML={{__html: text}} />
);

const TopBannerDefaultContent = () => {
  const intl = useIntl();

  return (
    <>
      <Link
        to={`tel:${intl.formatMessage({id: 'header.phone.raw'})}`}
        className='flex xxs:py-0 py-2 items-center xxs:text-sm text-lg text-white cursor-pointer'
      >
        <SlimIcon className='xss:mt-0 mt-2' name='phone' size='small' viewBoxSize='custom30x30' />
        <FormattedMessage
          id='header.topBanner.display'
          values={{
            salesLabel: (
              <span className='hidden md:block font-bold'>
                <FormattedMessage id='header.topBanner.sales.label' />
              </span>
            )
          }}
        />
      </Link>
      <span data-testid='top-banner-support' className='hidden md:flex ml-10 xxs:py-0 py-2 items-center xxs:text-sm text-lg textt'>
        <SlimIcon className='xss:mt-0 mt-2' name='support' size='small' viewBoxSize='custom30x30' />
        <FormattedMessage
          id='header.topBanner.support'
          values={{
            support: (
              <span className='font-bold'>
                <FormattedMessage id='header.topBanner.support.label' />
              </span>
            ),
            helpCenter: (
              <Link to={intl.formatMessage({id: 'header.topBanner.help_center.link'})}>
                <span className='text-white underline cursor-pointer'>
                  <FormattedMessage id='header.topBanner.help_center' />
                </span>
              </Link>
            )
          }}
        />
      </span>
    </>
  );
};
